import { cn } from "@portal/components/lib/utils";
import type { AssistantCommand } from "./assistantCopy";

/** Wide enough for a 10-character name in Roboto Mono at 14px. */
const NAME_COLUMN_MIN_PX = 84;
const NAME_COLUMN_MAX_PX = 140;

/**
 * The `/` command list, floating above the composer. One flat list: skills and commands render
 * alike, in source order. The composer owns the filter, the highlight and the keys.
 */
export const AdminAssistantCommandMenu = ({
	activeIndex,
	commands,
	draft,
	id,
	onHighlight,
	onPick,
}: {
	activeIndex: number;
	commands: readonly AssistantCommand[];
	/** What the underwriter typed, quoted when nothing matches it. */
	draft: string;
	/** Referenced by the textarea's `aria-controls` and `aria-activedescendant`. */
	id: string;
	onHighlight: (index: number) => void;
	onPick: (command: AssistantCommand) => void;
}) => {
	// One width for the whole list, so the descriptions line up in a single column.
	const longestName = Math.max(0, ...commands.map(({ command }) => command.length));
	const nameColumn = `clamp(${NAME_COLUMN_MIN_PX}px, ${longestName}ch, ${NAME_COLUMN_MAX_PX}px)`;

	return (
		<div
			aria-label="Commands"
			className="absolute right-0 bottom-[calc(100%+6px)] left-0 z-[2] flex max-h-[240px] flex-col gap-0.5 overflow-y-auto rounded-md border border-bc-default bg-surface-base p-1 shadow-floating-panel"
			data-testid="assistant-command-menu"
			id={id}
			role="listbox"
		>
			{commands.length === 0 ? (
				<div
					className="px-2 py-[7px] text-[12px] leading-[18px] font-medium text-tertiary"
					data-testid="assistant-command-empty"
				>
					No commands match “{draft}”
				</div>
			) : (
				commands.map((command, index) => (
					<div
						aria-selected={index === activeIndex}
						className={cn(
							"flex cursor-pointer items-baseline gap-2.5 rounded px-2 py-[7px]",
							index === activeIndex && "bg-surface-neutral",
						)}
						data-testid="assistant-command-option"
						id={`${id}-${index}`}
						key={command.command}
						onMouseEnter={() => onHighlight(index)}
						// mousedown, not click: picking must not blur the textarea.
						onMouseDown={(event) => {
							event.preventDefault();
							onPick(command);
						}}
						role="option"
					>
						<span
							className="flex-none font-mono text-[14px] leading-[20px] font-semibold whitespace-nowrap text-primary"
							style={{ width: nameColumn }}
						>
							{command.command}
						</span>
						<span className="min-w-0 flex-1 text-pretty text-[12px] leading-[18px] font-medium text-tertiary">
							{command.description}
						</span>
					</div>
				))
			)}
		</div>
	);
};
